import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import IntroWizard from "@/components/onboarding/IntroWizard";
import { PricingCards } from "@/components/pricing/PricingMatrixTable";
import { useLanguage } from "@/i18n/LanguageContext";
import { cn } from "@/lib/utils";

/**
 * Solo administrador: recorre el alta tal como la vería cada tipo de usuario
 * (visitante o registrado, con o sin plan elegido en la portada). Es una vista
 * previa: no guarda nada en el perfil ni inicia ningún cobro.
 */
type Account = "guest" | "registered";
type Entry = "free" | "basico" | "club_pro" | "completo";
type Stage = "setup" | "signup" | "wizard" | "gate" | "plan" | "done";

const PLAN_NAMES: Record<Exclude<Entry, "free">, { es: string; en: string }> = {
  basico: { es: "Básico · 39 €/mes", en: "Basic · €39/mo" },
  club_pro: { es: "Club Pro · 69 €/mes", en: "Club Pro · €69/mo" },
  completo: { es: "Avanzado · 99 €/mes", en: "Advanced · €99/mo" },
};

export default function AdminOnboardingSimulator({ onClose }: { onClose: () => void }) {
  const { lang } = useLanguage();
  const es = lang !== "en";
  const t = (a: string, b: string) => (es ? a : b);
  const [account, setAccount] = useState<Account>("guest");
  const [entry, setEntry] = useState<Entry>("free");
  const [stage, setStage] = useState<Stage>("setup");
  const [picked, setPicked] = useState<string | null>(null);

  const pill = (on: boolean) => cn(
    "min-h-11 rounded-xl border px-3 py-2 text-left font-body text-sm transition-colors",
    on ? "border-foreground bg-foreground text-background" : "border-border bg-card hover:bg-secondary",
  );
  const primary = "rounded-xl bg-foreground px-6 py-3 font-body text-sm font-semibold text-background";
  const secondary = "rounded-xl border border-border px-5 py-2.5 font-body text-sm hover:bg-secondary";
  const restart = () => { setPicked(null); setStage("setup"); };
  const afterWizard = () => setStage(entry === "free" ? "gate" : "plan");
  const planName = (k: string) => {
    const n = PLAN_NAMES[k as Exclude<Entry, "free">];
    return n ? (es ? n.es : n.en) : k;
  };
  const noClose = {
    onEscapeKeyDown: (e: Event) => e.preventDefault(),
    onInteractOutside: (e: Event) => e.preventDefault(),
  };

  if (stage === "wizard") return <IntroWizard open preview onClosePreview={afterWizard} />;

  return (
    <Dialog open onOpenChange={(o) => { if (!o && stage === "setup") onClose(); }}>
      <DialogContent className={cn("max-h-[92vh] overflow-y-auto", stage === "gate" ? "max-w-5xl [&>button]:hidden" : "max-w-lg")} {...(stage === "gate" ? noClose : {})}>
        <p className="font-body text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          {t("Vista previa del alta · no se guarda nada", "Onboarding preview · nothing is saved")}
        </p>

        {stage === "setup" && (
          <div className="space-y-5">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{t("¿Qué usuario quieres simular?", "Which user do you want to simulate?")}</DialogTitle>
              <DialogDescription>{t("Elige el caso y verás exactamente lo que le aparece.", "Pick the case and you'll see exactly what they get.")}</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <p className="font-body text-xs font-semibold">{t("1. Cuenta", "1. Account")}</p>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" className={pill(account === "guest")} onClick={() => setAccount("guest")}>{t("Visitante sin cuenta", "Visitor, no account")}</button>
                <button type="button" className={pill(account === "registered")} onClick={() => setAccount("registered")}>{t("Ya registrado", "Already registered")}</button>
              </div>
            </div>
            <div className="space-y-2">
              <p className="font-body text-xs font-semibold">{t("2. Por dónde entra", "2. How they enter")}</p>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" className={pill(entry === "free")} onClick={() => setEntry("free")}>{t("«Empezar gratis» (sin plan)", "“Start free” (no plan)")}</button>
                {(Object.keys(PLAN_NAMES) as Exclude<Entry, "free">[]).map((k) => (
                  <button key={k} type="button" className={pill(entry === k)} onClick={() => setEntry(k)}>{t("Ficha ", "Card ")}{planName(k)}</button>
                ))}
              </div>
            </div>
            <div className="flex justify-between gap-3">
              <button type="button" className={secondary} onClick={onClose}>{t("Cerrar", "Close")}</button>
              <button type="button" className={primary} onClick={() => setStage(account === "guest" ? "signup" : "wizard")}>{t("Empezar simulación", "Start simulation")}</button>
            </div>
          </div>
        )}

        {stage === "signup" && (
          <div className="space-y-5">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{t("Paso 0 · Registro", "Step 0 · Sign-up")}</DialogTitle>
              <DialogDescription>
                {t("El visitante crea su cuenta con Google (One Tap o botón) o con email. Su nombre de Google rellena los pasos iniciales.",
                  "The visitor creates an account with Google (One Tap or button) or email. Their Google name pre-fills the first steps.")}
              </DialogDescription>
            </DialogHeader>
            {entry !== "free" && (
              <p className="rounded-xl border border-border bg-secondary/50 p-3 font-body text-sm">
                {t("Plan guardado de la portada: ", "Plan saved from the home page: ")}<strong>{planName(entry)}</strong>
              </p>
            )}
            <div className="flex justify-end">
              <button type="button" className={primary} onClick={() => setStage("wizard")}>{t("Continuar a los pasos iniciales", "Continue to first steps")}</button>
            </div>
          </div>
        )}

        {stage === "gate" && (
          <div className="space-y-4">
            <DialogHeader className="text-center sm:text-center">
              <DialogTitle className="font-display text-2xl">{t("¡Tu panel ya está listo! Elige tu plan para entrar", "Your panel is ready! Choose your plan to get in")}</DialogTitle>
              <DialogDescription>{t("Tu Agente Retail IA ya conoce tu negocio. Activa tu plan y empieza hoy mismo.", "Your Retail AI Agent already knows your business. Activate your plan and start today.")}</DialogDescription>
              <p className="text-xs text-muted-foreground">{t("7 días de prueba gratis · Sin permanencia · Cancela en 1 clic", "7-day free trial · No commitment · Cancel in 1 click")}</p>
            </DialogHeader>
            <PricingCards carousel centerKey="club_pro" onSelectOverride={(k) => { setPicked(k); setStage("done"); }} />
          </div>
        )}

        {stage === "plan" && (
          <div className="space-y-5">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{t("Sin ventana de planes", "No plan window")}</DialogTitle>
              <DialogDescription>
                {t("Como eligió una ficha en la portada, tras el avatar va directo a activar su plan:", "Since they picked a card on the home page, after the avatar they go straight to activate their plan:")}
              </DialogDescription>
            </DialogHeader>
            <p className="rounded-xl border border-border bg-secondary/50 p-3 font-body text-sm"><strong>{planName(entry)}</strong></p>
            <div className="flex justify-end"><button type="button" className={primary} onClick={() => { setPicked(entry); setStage("done"); }}>{t("Ver siguiente paso", "See next step")}</button></div>
          </div>
        )}

        {stage === "done" && (
          <div className="space-y-5">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">{t("Fin de la simulación", "End of simulation")}</DialogTitle>
              <DialogDescription>
                {t("Aquí el usuario pasaría al pago real de ", "Here the user would go to the real checkout for ")}<strong>{planName(picked ?? "")}</strong>
                {t(" con 7 días gratis y, al terminar, entraría en su panel.", " with a 7-day free trial and then enter their panel.")}
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-between gap-3">
              <button type="button" className={secondary} onClick={restart}>{t("Probar otro caso", "Try another case")}</button>
              <button type="button" className={primary} onClick={onClose}>{t("Terminar", "Finish")}</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
